import {
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerHeader,
  DrawerBody,
  HStack,
  Text,
  Button,
  Stack,
  Divider,
  Link,
  Heading,
  Menu,
  MenuButton,
  MenuList,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Flex,
  useDisclosure,
} from '@chakra-ui/react';
import _ from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
  date as commonDate,
  finleyAssertUnreachable,
} from '@finley-technologies/common';

import { ThreadGQLType } from 'types/dashboard-core-gqlTypes.generated.d';

import { CommentThread } from './CommentThread';
import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';
import { colors } from '~/packages/common-frontend/colors';
import {
  CommentEditorInput,
  CommentEditorCancelButton,
  CommentEditorCreateButton,
  CommentEditor,
} from '~/packages/common-frontend/components/Comments/CommentEditor';
import { ExternalOrgSelector } from '~/packages/common-frontend/components/Comments/ExternalOrgSelector';
import { Counter } from '~/packages/common-frontend/components/Counter';
import { Icon } from '~/packages/common-frontend/components/icons';
import { MultiSelect } from '~/packages/common-frontend/components/MultiSelect';
import {
  Select,
  formatSelectOptions,
} from '~/packages/common-frontend/components/Select';
import { Typography } from '~/packages/common-frontend/components/Typography';

const audienceFilterOptions = ['all', 'internal', 'external'] as const;
type AudienceFilterType = typeof audienceFilterOptions[number];

type FilterType = {
  audienceFilter: AudienceFilterType;
  showParticipantFilter: boolean;
  titleText: string;
  externalParticipantIds: string[];
};

const FilterButton = ({
  externalOrgOptions,
  onApply,
  currentFilter,
}: {
  externalOrgOptions: { value: string; label: string }[];
  onApply: (newCurrentFilter: FilterType) => void;
  currentFilter: FilterType | undefined;
}) => {
  // audience filter
  const [audienceFilter, setAudienceFilter] = useState<AudienceFilterType>(
    currentFilter ? currentFilter.audienceFilter : 'all',
  );

  // participant filter
  const [showParticipantFilter, setShowParticipantFilter] = useState<boolean>(
    currentFilter ? currentFilter.showParticipantFilter : true,
  );
  const [titleText, setTitleText] = useState<string>(
    currentFilter ? currentFilter.titleText : 'All Participants',
  );
  const allExternalParticipantIds = externalOrgOptions.map(
    (option) => option.value,
  );
  const [selectedParticipantIds, setSelectedExternalPartyIds] = useState<
    string[]
  >(
    currentFilter
      ? currentFilter.externalParticipantIds
      : allExternalParticipantIds,
  );

  const onChange = (newValue: string) => {
    const copyOfSelectedParticipantIds = _.clone(selectedParticipantIds);
    const index = copyOfSelectedParticipantIds.indexOf(newValue);
    if (index >= 0) {
      // only remove option if multiple are selected
      if (copyOfSelectedParticipantIds.length > 1) {
        copyOfSelectedParticipantIds.splice(index, 1);
      }
    } else {
      copyOfSelectedParticipantIds.push(newValue);
    }
    setSelectedExternalPartyIds(copyOfSelectedParticipantIds);

    if (copyOfSelectedParticipantIds.length > 0) {
      if (copyOfSelectedParticipantIds.length === externalOrgOptions.length) {
        setTitleText('All Participants');
      } else {
        const selectedOptions = externalOrgOptions.filter((option) =>
          copyOfSelectedParticipantIds.includes(option.value),
        );
        const labels = selectedOptions.map((option) =>
          _.startCase(option.label),
        );
        setTitleText(labels.join(', '));
      }
    } else {
      setTitleText('Select Participants');
    }
  };

  // use effect on select change
  useEffect(() => {
    if (audienceFilter === 'internal') {
      setShowParticipantFilter(false);
      setSelectedExternalPartyIds([]);
    } else {
      setShowParticipantFilter(true);
      setSelectedExternalPartyIds((curr) => {
        if (curr.length === 0) {
          return allExternalParticipantIds;
        }
        return curr;
      });
    }
  }, [allExternalParticipantIds, audienceFilter]);

  const onSubmit = useCallback(() => {
    switch (audienceFilter) {
      case 'all':
        return onApply({
          audienceFilter,
          showParticipantFilter,
          titleText,
          externalParticipantIds: selectedParticipantIds,
        });
      case 'external':
        return onApply({
          audienceFilter,
          showParticipantFilter,
          titleText,
          externalParticipantIds: selectedParticipantIds,
        });
      case 'internal':
        return onApply({
          audienceFilter,
          showParticipantFilter: false,
          titleText,
          externalParticipantIds: [],
        });
      default:
        return finleyAssertUnreachable(audienceFilter);
    }
  }, [
    audienceFilter,
    onApply,
    selectedParticipantIds,
    showParticipantFilter,
    titleText,
  ]);

  const onClear = useCallback(() => {
    setAudienceFilter(currentFilter ? currentFilter.audienceFilter : 'all');
    setShowParticipantFilter(
      currentFilter ? currentFilter.showParticipantFilter : true,
    );
    setTitleText(currentFilter ? currentFilter.titleText : 'All Participants');
    setSelectedExternalPartyIds(
      currentFilter
        ? currentFilter.externalParticipantIds
        : allExternalParticipantIds,
    );
  }, [allExternalParticipantIds, currentFilter]);

  return (
    <Menu autoSelect={false} eventListeners={{ scroll: true, resize: true }}>
      {({ onClose }) => (
        <>
          <MenuButton
            as={Button}
            leftIcon={<Icon icon="filter" size="small" />}
            size="sm"
            variant="secondary-muted"
            colorScheme="gray"
          >
            Filter
          </MenuButton>
          <MenuList
            p="16px"
            minW="300"
            maxW="800"
            left={0}
            color={colors.aurora[400]}
            backgroundColor={colors.millet[50]}
            fontSize="16px"
          >
            <Heading
              fontSize="sm"
              fontWeight="medium"
              mb="16px"
              textTransform="none"
            >
              Filter
            </Heading>
            <Stack fontSize="sm">
              <Text fontSize="sm">Audience</Text>
              <Select
                value={audienceFilter}
                onChange={(newVal) => {
                  if (newVal) {
                    setAudienceFilter(newVal);
                  }
                }}
                options={formatSelectOptions(audienceFilterOptions, 'title')}
                buttonProps={{ fontSize: 'small', size: 'md' }}
              />
              {showParticipantFilter && (
                <>
                  <Text fontSize="sm">Participants</Text>
                  <MultiSelect
                    options={externalOrgOptions}
                    onChange={onChange}
                    selectedOptions={selectedParticipantIds}
                    titleText={titleText}
                    size="sm"
                  />
                </>
              )}
            </Stack>
            <Divider width="90%" margin="16px auto" />
            <HStack width="full" pl={1} pr={1}>
              <Button
                size="xs"
                py={4}
                px={12}
                justifyContent="flex-start"
                variant="outline"
                borderColor={colors.aurora[400]}
                borderWidth="2px"
                color={colors.aurora[400]}
                _focus={{ boxShadow: 'none' }}
                onClick={() => {
                  onClear();
                  onClose();
                }}
              >
                Clear
              </Button>
              <Button
                size="xs"
                py={4}
                px={12}
                ml={4}
                borderColor={colors.aurora[400]}
                borderWidth="2px"
                justifyContent="flex-start"
                variant="solid"
                _focus={{ boxShadow: 'none' }}
                colorScheme="aurora"
                color="white"
                onClick={() => {
                  onSubmit();
                  onClose();
                }}
              >
                Apply
              </Button>
            </HStack>
          </MenuList>
        </>
      )}
    </Menu>
  );
};

const UpdatesDrawerBody = ({
  threads,
  refetchThreads,
  onCreateNewCommentOnExistingThread,
}: {
  threads: ThreadGQLType[];
  refetchThreads: () => void;
  onCreateNewCommentOnExistingThread: (newCommentId: string) => Promise<void>;
}) => {
  const sortedThreads = useMemo(
    () => threads.sort((a, b) => b.created_at - a.created_at),
    [threads],
  );

  const threadsGroupedByMonth = _.groupBy(sortedThreads, (e) => {
    const fullDateStr = commonDate.epochToFullDateStr(e.created_at);
    const datetime = commonDate.fullDateStrToDateTime(fullDateStr);

    return `${datetime.monthLong} ${datetime.year}`;
  });

  return (
    <>
      {Object.entries(threadsGroupedByMonth).map(([key, values]) => {
        return (
          <Stack key={key} width="full">
            <Text
              fontSize="sm"
              width="full"
              textAlign="center"
              fontWeight="bold"
              mb={2}
            >
              {key}
            </Text>
            {values.map((val) => {
              return (
                <React.Fragment key={JSON.stringify(val)}>
                  <Stack mb={4}>
                    <CommentThread
                      thread={val}
                      refetchThread={refetchThreads}
                      onCreateNewComment={async (newCommentId) => {
                        await onCreateNewCommentOnExistingThread(newCommentId);
                        refetchThreads();
                      }}
                    />
                  </Stack>
                </React.Fragment>
              );
            })}
          </Stack>
        );
      })}
    </>
  );
};

export const DetailedUpdatesDrawer = ({
  isOpen,
  onClose,
  threads,
  refetchThreads,
  externalOrgOptions,
  createNewThreadWithCommentForDomain,
  onCreateNewCommentOnExistingThread,
  children = <></>,
}: {
  isOpen: boolean;
  onClose: () => void;
  threads: ThreadGQLType[];
  refetchThreads: () => void;
  externalOrgOptions: { value: string; label: string }[];
  createNewThreadWithCommentForDomain: (params: {
    organizationIds: string[];
    comment: string;
    authorUserId: string;
  }) => Promise<void>;
  onCreateNewCommentOnExistingThread: (newCommentId: string) => Promise<void>;
  children?: React.ReactNode;
}) => {
  const { user } = useUserAuthOrThrow();
  const externalDisclosure = useDisclosure({ defaultIsOpen: false });
  const [selectedExternalPartyIds, setSelectedExternalPartyIds] = useState<
    string[]
  >([]);

  const [currentFilter, setCurrentFilter] = useState<FilterType>();

  const allThreads = useMemo(() => {
    const threadsWithComments = threads.filter(
      ({ comments }) => comments.length > 0,
    );
    if (currentFilter) {
      const {
        audienceFilter: audienceType,
        externalParticipantIds,
      } = currentFilter;
      const filteredThreads = threadsWithComments.filter((t) => {
        const orgIdsForThread = t.threadPermissions.map(
          (tp) => tp.organizationId,
        );

        const threadIsInternal =
          orgIdsForThread.length === 1 &&
          orgIdsForThread[0] === user.organization.id;
        const threadIsExternal =
          _.difference(externalParticipantIds.sort(), orgIdsForThread.sort())
            .length === 0;

        switch (audienceType) {
          case 'internal':
            return threadIsInternal;
          case 'external':
            return threadIsExternal;
          case 'all':
            return threadIsInternal || threadIsExternal;
          default:
            return finleyAssertUnreachable(audienceType);
        }
      });

      return filteredThreads;
    }
    return threadsWithComments;
  }, [currentFilter, threads, user.organization.id]);

  const pinnedThreads = useMemo(
    () =>
      threads.filter((thread) => thread.pinned && thread.comments.length > 0),
    [threads],
  );

  const [tabIndex, setTabIndex] = useState(0);

  const onCreateComment = async (comment: string) => {
    const organizationIds = [
      ...new Set([user.organization.id, ...selectedExternalPartyIds]),
    ];
    await createNewThreadWithCommentForDomain({
      organizationIds,
      comment,
      authorUserId: user.id,
    });

    refetchThreads();
  };

  return (
    <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="lg">
      <DrawerOverlay />
      <DrawerContent pt={2}>
        <DrawerCloseButton />
        <DrawerHeader>
          <HStack>
            <Text fontSize="24px">Updates</Text>
            {children}
          </HStack>
        </DrawerHeader>

        <DrawerBody>
          <Flex direction="column" gap="1rem" alignItems="flex-start">
            <CommentEditor
              onCreate={onCreateComment}
              isDisabled={
                externalDisclosure.isOpen &&
                selectedExternalPartyIds.length === 0
              }
              left={
                <ExternalOrgSelector
                  disclosure={externalDisclosure}
                  externalOrgOptions={externalOrgOptions}
                  selectedIds={selectedExternalPartyIds}
                  onChange={setSelectedExternalPartyIds}
                />
              }
              right={
                <>
                  <CommentEditorCancelButton />
                  <CommentEditorCreateButton>
                    Send Comment
                  </CommentEditorCreateButton>
                </>
              }
            >
              <CommentEditorInput isEditable />
            </CommentEditor>

            <Tabs
              index={pinnedThreads.length === 0 ? 0 : tabIndex}
              onChange={(index) => setTabIndex(index)}
              width="full"
            >
              <Flex gap="0.5rem">
                <TabList flexGrow={1}>
                  <Tab marginTop={0}>All</Tab>
                  {pinnedThreads.length > 0 && (
                    <Tab marginTop={0}>
                      <HStack>
                        <Typography trim>Pinned</Typography>
                        <Counter count={pinnedThreads.length} />
                      </HStack>
                    </Tab>
                  )}
                </TabList>
                {externalOrgOptions.length > 0 && (
                  <FilterButton
                    externalOrgOptions={externalOrgOptions}
                    onApply={(newCurrentFilter) => {
                      setCurrentFilter(newCurrentFilter);
                    }}
                    currentFilter={currentFilter}
                  />
                )}
              </Flex>

              <TabPanels py="1rem">
                <TabPanel padding="0">
                  <UpdatesDrawerBody
                    threads={allThreads}
                    refetchThreads={refetchThreads}
                    onCreateNewCommentOnExistingThread={
                      onCreateNewCommentOnExistingThread
                    }
                  />
                </TabPanel>
                {pinnedThreads.length > 0 && (
                  <TabPanel padding="0">
                    <UpdatesDrawerBody
                      threads={pinnedThreads}
                      refetchThreads={refetchThreads}
                      onCreateNewCommentOnExistingThread={
                        onCreateNewCommentOnExistingThread
                      }
                    />
                  </TabPanel>
                )}
              </TabPanels>
            </Tabs>
          </Flex>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};

const OverviewUpdatesDrawerBody = ({
  threadsWithMetadata,
  refetchThreads,
  onCreateNewCommentOnExistingThread,
}: {
  threadsWithMetadata: {
    thread: ThreadGQLType;
    metadata: {
      title: string;
      subtitle?: string;
      link: string;
    };
  }[];
  refetchThreads: () => void;
  onCreateNewCommentOnExistingThread: (newCommentId: string) => Promise<void>;
}) => {
  const sortedThreads = useMemo(
    () =>
      threadsWithMetadata.sort(
        (a, b) => b.thread.created_at - a.thread.created_at,
      ),
    [threadsWithMetadata],
  );

  return (
    <Flex direction="column" gap="1rem" alignItems="flex-start">
      {sortedThreads.length === 0 && (
        <Typography variant="base-muted">There are no comments.</Typography>
      )}

      {sortedThreads.map(({ thread, metadata }) => {
        return (
          <CommentThread
            key={thread.threadId}
            thread={thread}
            refetchThread={refetchThreads}
            onCreateNewComment={async (newCommentId) => {
              await onCreateNewCommentOnExistingThread(newCommentId);
              refetchThreads();
            }}
          >
            <Flex justifyContent="space-between" gap="0.5rem">
              <Link
                fontSize="sm"
                fontWeight="bold"
                color="blue.400"
                href={metadata.link}
              >
                {metadata.title}
              </Link>
              {metadata.subtitle && (
                <Typography variant="detail">{metadata.subtitle}</Typography>
              )}
            </Flex>
            <Divider />
          </CommentThread>
        );
      })}
    </Flex>
  );
};

export const OverviewUpdatesDrawer = ({
  isOpen,
  onClose,
  threadsWithMetadata,
  externalOrgOptions,
  refetchThreads,
  onCreateNewCommentOnExistingThread,
}: {
  isOpen: boolean;
  onClose: () => void;
  threadsWithMetadata: {
    thread: ThreadGQLType;
    metadata: {
      title: string;
      subtitle?: string;
      link: string;
    };
  }[];
  externalOrgOptions: { value: string; label: string }[];
  refetchThreads: () => void;
  onCreateNewCommentOnExistingThread: (newCommentId: string) => Promise<void>;
}) => {
  const { user } = useUserAuthOrThrow();
  // apply filter here
  const [currentFilter, setCurrentFilter] = useState<FilterType>();
  const threads = useMemo(() => {
    // only surface threads with comments
    const threadsWithComments = threadsWithMetadata.filter(
      ({ thread: { comments } }) => comments.length > 0,
    );

    if (currentFilter) {
      const {
        audienceFilter: audienceType,
        externalParticipantIds,
      } = currentFilter;
      const filteredThreads = threadsWithComments.filter((t) => {
        const orgIdsForThread = t.thread.threadPermissions.map(
          (tp) => tp.organizationId,
        );

        const threadIsInternal =
          orgIdsForThread.length === 1 &&
          orgIdsForThread[0] === user.organization.id;
        const threadIsExternal =
          _.difference(externalParticipantIds.sort(), orgIdsForThread.sort())
            .length === 0;

        switch (audienceType) {
          case 'internal':
            return threadIsInternal;
          case 'external':
            return threadIsExternal;
          case 'all':
            return threadIsInternal || threadIsExternal;
          default:
            return finleyAssertUnreachable(audienceType);
        }
      });

      return filteredThreads;
    }
    return threadsWithComments;
  }, [currentFilter, threadsWithMetadata, user.organization.id]);

  const pinnedThreads = useMemo(
    () =>
      threads.filter(
        (thread) => thread.thread.pinned && thread.thread.comments.length > 0,
      ),
    [threads],
  );

  const [tabIndex, setTabIndex] = useState(0);

  return (
    <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="lg">
      <DrawerOverlay />
      <DrawerContent pt={2}>
        <DrawerCloseButton />
        <DrawerHeader>Updates</DrawerHeader>
        <DrawerBody>
          <Tabs
            size="sm"
            index={pinnedThreads.length === 0 ? 0 : tabIndex}
            onChange={(index) => setTabIndex(index)}
          >
            <Flex gap="0.5rem" alignItems="center">
              <TabList flexGrow={1}>
                <Tab marginTop={0}>All</Tab>
                {pinnedThreads.length > 0 && (
                  <Tab marginTop={0}>
                    <span>Pinned</span>
                    <Counter count={pinnedThreads.length} />
                  </Tab>
                )}
              </TabList>
              {externalOrgOptions.length > 0 && (
                <FilterButton
                  externalOrgOptions={externalOrgOptions}
                  onApply={setCurrentFilter}
                  currentFilter={currentFilter}
                />
              )}
            </Flex>
            <TabPanels py="1rem">
              <TabPanel padding="0">
                <OverviewUpdatesDrawerBody
                  threadsWithMetadata={threads}
                  refetchThreads={refetchThreads}
                  onCreateNewCommentOnExistingThread={
                    onCreateNewCommentOnExistingThread
                  }
                />
              </TabPanel>
              {pinnedThreads.length > 0 && (
                <TabPanel padding="0">
                  <OverviewUpdatesDrawerBody
                    threadsWithMetadata={pinnedThreads}
                    refetchThreads={refetchThreads}
                    onCreateNewCommentOnExistingThread={
                      onCreateNewCommentOnExistingThread
                    }
                  />
                </TabPanel>
              )}
            </TabPanels>
          </Tabs>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};
